import {
  distancePointToSegment,
  insetPolygon,
  localToWorld,
  pointInOrOnPolygon,
  pointInPolygon,
  pointOnPolygonBoundary,
  polygonArea,
  rotate,
  subtractPolygons,
  type Region,
  type Vec2,
} from '@indusketch/geometry';
import {
  buildingCorners,
  removedSides,
  storeyCorners,
  storeyOutline,
  storeyWallGeometry,
} from './building.js';
import { groundElevation } from './derived/dock.js';
import { FLAT_ROOF_THICKNESS, type RoofOpening } from './derived/roof.js';
import { wallFrame } from './derived/walls.js';
import type {
  Building,
  Mezzanine,
  Office,
  Opening,
  PlacedObject,
  ProjectDocument,
  Stair,
  StairType,
  Wall,
} from './types.js';
import { fillText, modelTexts } from './texts.js';
import { formatNumber } from './units.js';

export const DEFAULT_CLEAR_HEIGHT = 2.7;
export const DEFAULT_SLAB_THICKNESS = 0.25;
export const DEFAULT_OFFICE_WALL_THICKNESS = 0.24;
export const DEFAULT_OFFICE_COLOR = '#d9d4c7';
export const DEFAULT_STAIR_WIDTH = 1;
/** Gap between the two flights of a U stair (stairwell eye). */
export const STAIR_EYE = 0.1;
/** Railing height on stairs and a usable top slab. */
export const RAILING_HEIGHT = 1.1;

/** Planning guide values of ASR A1.8 (Verkehrswege) for stairs; hints, not a code check. */
export const STAIR_RULES = {
  riserMin: 0.14,
  riserMax: 0.19,
  riserTarget: 0.175,
  goingMin: 0.26,
  goingMax: 0.32,
  stepMin: 0.59,
  stepMax: 0.65,
  widthMin: 1,
} as const;

export const officeHeight = (o: Office): number =>
  o.floors.reduce((s, f) => s + f.clearHeight + o.slabThickness, 0);

/**
 * Outline of an office's floor slabs: they rest on the outer walls up to their outer face, so
 * the slab edge shows on the facade. Where a ground floor wall was removed nothing carries the
 * slab, so it ends one wall thickness earlier (where the wall's inner face was).
 */
export function officeSlabOutline(
  b: Pick<Building, 'width' | 'depth' | 'center' | 'rotation'> &
    Partial<Pick<Building, 'omittedWalls' | 'wallThickness' | 'footprint' | 'office'>>,
): Vec2[] {
  const t = b.wallThickness ?? 0;
  // Exterior wall i runs along footprint edge i (the rectangle's sides counted from south).
  const corners = buildingCorners(b);
  const n = corners.length;
  const gone = removedSides({ omittedWalls: b.omittedWalls ?? [], office: b.office ?? null });
  const removed = (i: number) => gone.includes(i);
  const inset = (i: number) => (removed(i) ? t : 0);
  if (corners.every((_, i) => inset(i) === 0)) return corners;
  // Each edge moved inwards by its own distance; neighbouring offset edges meet in a corner.
  const line = (i: number) => {
    const a = corners[i] as Vec2;
    const c = corners[(i + 1) % n] as Vec2;
    const len = Math.hypot(c.x - a.x, c.y - a.y) || 1;
    const dir = { x: (c.x - a.x) / len, y: (c.y - a.y) / len };
    // Inward normal of a counter-clockwise edge points to its left.
    const d = inset(i);
    return { p: { x: a.x - dir.y * d, y: a.y + dir.x * d }, dir };
  };
  return corners.map((_, i) => {
    const l1 = line((i - 1 + n) % n);
    const l2 = line(i);
    const det = l1.dir.x * l2.dir.y - l1.dir.y * l2.dir.x;
    if (Math.abs(det) < 1e-9) return l2.p;
    const s = ((l2.p.x - l1.p.x) * l2.dir.y - (l2.p.y - l1.p.y) * l2.dir.x) / det;
    return { x: l1.p.x + l1.dir.x * s, y: l1.p.y + l1.dir.y * s };
  });
}

/**
 * Outline of the slab carrying `level` (and covering the storey below): as big as that storey
 * below, so where an upper storey is smaller (setback) the slab is the roof next to it.
 */
export function officeLevelSlab(b: Building, level: number): Vec2[] {
  return storeyOutline(b, level - 1) ?? officeSlabOutline(b);
}

/** Floors whose slab lies on the outer walls (an outdoor office's top is its roof instead). */
export const officeSlabLevels = (o: Office): number[] =>
  Array.from(
    { length: o.placement === 'indoor' ? o.floors.length : o.floors.length - 1 },
    (_, i) => i + 1,
  );

/** How far a slab reaches into the exterior walls it lies on. */
export const SLAB_BEARING = 0.02;

/**
 * Outline of the slab carrying `level` as built: it lies `SLAB_BEARING` deep on the exterior
 * walls of the storey below, which run up past it, so it is not seen from outside.
 */
export function officeSlabBearing(b: Building, level: number): Vec2[] {
  return insetPolygon(officeLevelSlab(b, level), Math.max(0, b.wallThickness - SLAB_BEARING));
}

/** Free area next to a smaller upper storey: roof (or roof terrace) on the storey below. */
export interface SetbackArea {
  /** The storey standing on this roof level; the area lies at that storey's floor height. */
  readonly level: number;
  readonly terrace: boolean;
  readonly regions: readonly Region[];
  /** Outline of the storey below, whose edge the parapet or railing follows. */
  readonly below: readonly Vec2[];
}

/** Roof areas left free by the setbacks of an office, lowest first. */
export function officeSetbackAreas(b: Building): SetbackArea[] {
  const o = b.office;
  if (!o) return [];
  return [...o.setbacks]
    .filter((s) => s.level < o.floors.length)
    .sort((x, y) => x.level - y.level)
    .flatMap((s) => {
      const below = storeyCorners(b, s.level - 1);
      const regions = subtractPolygons(below, [storeyCorners(b, s.level)]).filter(
        (r) => polygonArea(r.outer) > 0.05,
      );
      return regions.length > 0 ? [{ level: s.level, terrace: s.terrace, regions, below }] : [];
    });
}

/** Edges of a region lying on `outline` (its outer edge, not along the storey above). */
export function edgesOnOutline(
  ring: readonly Vec2[],
  outline: readonly Vec2[],
): { a: Vec2; b: Vec2 }[] {
  const out: { a: Vec2; b: Vec2 }[] = [];
  ring.forEach((a, i) => {
    const b = ring[(i + 1) % ring.length] as Vec2;
    const mid = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    if (pointOnPolygonBoundary(mid, outline, 0.05)) out.push({ a, b });
  });
  return out;
}

/** Point `d` inwards from the edge a→b of a counter-clockwise ring. */
const inward = (a: Vec2, b: Vec2, d: number): [Vec2, Vec2] => {
  const len = Math.hypot(b.x - a.x, b.y - a.y) || 1;
  const n = { x: (-(b.y - a.y) / len) * d, y: ((b.x - a.x) / len) * d };
  return [
    { x: a.x + n.x, y: a.y + n.y },
    { x: b.x + n.x, y: b.y + n.y },
  ];
};

/**
 * Guard railings of an office (absolute base height): where an upper storey's exterior wall
 * piece was removed, and along the outer edge of roof terraces next to a smaller storey.
 */
export function officeOpenRailings(
  _doc: Pick<ProjectDocument, 'buildings'>,
  b: Building,
): { a: Vec2; b: Vec2; base: number; level: number }[] {
  const o = b.office;
  if (!o) return [];
  const e = levelElevations(o);
  const out: { a: Vec2; b: Vec2; base: number; level: number }[] = [];
  for (const f of o.openFloors) {
    if (!f.railing || f.level < 1 || f.level >= o.floors.length) continue;
    const g = storeyWallGeometry(b, f.level)[f.wall];
    if (g) out.push({ a: g.start, b: g.end, base: e[f.level] ?? 0, level: f.level });
  }
  for (const area of officeSetbackAreas(b)) {
    if (!area.terrace) continue;
    for (const r of area.regions)
      for (const edge of edgesOnOutline(r.outer, area.below)) {
        const [a, c] = inward(edge.a, edge.b, b.wallThickness / 2);
        out.push({ a, b: c, base: e[area.level] ?? 0, level: area.level });
      }
  }
  return out;
}

/** Floor area of one storey of an office (its own outline where it is smaller). */
export const storeyArea = (b: Building, level: number): number =>
  polygonArea(storeyCorners(b, level));

/** Floor elevation of every level; the last entry is the top of the building (top slab). */
export function levelElevations(o: Office): number[] {
  const out = [0];
  for (const f of o.floors) out.push((out[out.length - 1] ?? 0) + f.clearHeight + o.slabThickness);
  return out;
}

/** Highest reachable level: the top slab of an indoor office counts if it is usable. */
export const topLevel = (b: Pick<Building, 'office'>): number =>
  b.office
    ? b.office.floors.length - 1 + (b.office.placement === 'indoor' && b.office.usableTop ? 1 : 0)
    : 0;

export const isOffice = (b: Building): b is Building & { office: Office } =>
  b.kind === 'office' && b.office !== null;

export function levelLabel(level: number, b?: Pick<Building, 'office'>): string {
  const t = modelTexts().levels;
  if (b?.office && level === b.office.floors.length) return t.top.replace('{n}', String(level));
  return level === 0 ? t.ground : t.upper.replace('{n}', String(level));
}

/** What the level functions read: offices and, where present, the mezzanines in halls. */
export type LevelDoc = Pick<ProjectDocument, 'buildings'> & {
  readonly mezzanines?: ProjectDocument['mezzanines'];
  readonly levelBackgrounds?: ProjectDocument['levelBackgrounds'];
};

const mezzaninesOf = (doc: LevelDoc): Mezzanine[] => Object.values(doc.mezzanines ?? {});

/** The mezzanine whose outline contains `p`. */
export function mezzanineAt(doc: LevelDoc, p: Vec2): Mezzanine | null {
  return mezzaninesOf(doc).find((m) => pointInOrOnPolygon(p, m.polygon, 1e-6)) ?? null;
}

/** Name of a level in the floor switch; a level only reached as a top slab is the platform. */
export function documentLevelLabel(doc: LevelDoc, level: number): string {
  const offices = Object.values(doc.buildings).filter((b) => b.office);
  const isFloor =
    level === 0 ||
    (level === 1 && mezzaninesOf(doc).length > 0) ||
    offices.some((b) => (b.office?.floors.length ?? 0) > level) ||
    (doc.levelBackgrounds ?? []).some((b) => b.level === level);
  return isFloor ? levelLabel(level) : modelTexts().levels.top.replace('{n}', String(level));
}

/**
 * Height of the horizontal section for looking into `level` in 3D: just below the slab above
 * that floor (the highest such height among all offices), or above the railing of a top
 * platform. Everything higher (slabs, upper floors, roofs) is cut away.
 */
export function sectionHeight(doc: LevelDoc, level: number): number | null {
  let h: number | null = null;
  const take = (cut: number) => {
    h = h === null ? cut : Math.max(h, cut);
  };
  for (const b of Object.values(doc.buildings)) {
    if (!b.office || topLevel(b) < level) continue;
    const e = levelElevations(b.office)[level] ?? 0;
    const clear = b.office.floors[level]?.clearHeight;
    take(clear === undefined ? e + RAILING_HEIGHT + 0.6 : e + clear - 0.01);
  }
  // Under a mezzanine the cut runs just below its slab; on it the hall is open up to the eaves.
  for (const m of mezzaninesOf(doc)) {
    const hall = doc.buildings[m.buildingId];
    if (level === 0) take(m.elevation - m.slabThickness - 0.01);
    else if (level === 1 && hall) take(hall.eaveHeight - 0.05);
  }
  return h;
}

/** Levels offered by the floor switch: EG plus every upper floor any office or mezzanine has. */
export function documentLevels(doc: LevelDoc): number[] {
  const max = Math.max(
    0,
    mezzaninesOf(doc).length > 0 ? 1 : 0,
    ...Object.values(doc.buildings).map((b) => topLevel(b)),
    // A floor plan laid in for an upper floor, to trace it before anything stands there.
    ...(doc.levelBackgrounds ?? []).map((b) => b.level),
  );
  return Array.from({ length: max + 1 }, (_, i) => i);
}

/** The hall an indoor office stands in (by its center), if any. */
export function hostHall(doc: Pick<ProjectDocument, 'buildings'>, b: Building): Building | null {
  for (const h of Object.values(doc.buildings)) {
    if (h.kind === 'hall' && pointInPolygon(b.center, buildingCorners(h))) return h;
  }
  return null;
}

/** The office whose footprint contains `p` and that reaches `level`. */
export function officeAt(
  doc: Pick<ProjectDocument, 'buildings'>,
  level: number,
  p: Vec2,
): Building | null {
  let fallback: Building | null = null;
  for (const b of Object.values(doc.buildings)) {
    if (!b.office || topLevel(b) < level) continue;
    if (pointInPolygon(p, buildingCorners(b))) return b;
    fallback ??= b;
  }
  return fallback;
}

/**
 * Floor elevation of `level` at `p`: the office there (or else the first office reaching that
 * level) defines it. Level 0 is always the ground.
 */
export function levelElevation(doc: LevelDoc, level: number, p: Vec2): number {
  if (level <= 0) return 0;
  const b = officeAt(doc, level, p);
  const inOffice = b?.office && pointInPolygon(p, buildingCorners(b));
  if (!inOffice && level === 1) {
    const m = mezzanineAt(doc, p) ?? (b ? null : (mezzaninesOf(doc)[0] ?? null));
    if (m) return m.elevation;
  }
  return b?.office ? (levelElevations(b.office)[level] ?? 0) : 0;
}

/**
 * Level of the floor a picked point lies on: the highest floor at `p` (hall floor, mezzanine
 * or office storey) at or a little below `height`.
 */
export function levelAtHeight(doc: LevelDoc, p: Vec2, height: number): number {
  let best = 0;
  let bestElevation = 0;
  for (const level of documentLevels(doc)) {
    if (level === 0) continue;
    const b = officeAt(doc, level, p);
    const inOffice = b?.office && pointInPolygon(p, buildingCorners(b));
    if (!inOffice && !(level === 1 && mezzanineAt(doc, p))) continue;
    const e = levelElevation(doc, level, p);
    if (e <= height + 0.3 && e > bestElevation) {
      best = level;
      bestElevation = e;
    }
  }
  return best;
}

const mid = (w: Pick<Wall, 'start' | 'end'>): Vec2 => ({
  x: (w.start.x + w.end.x) / 2,
  y: (w.start.y + w.end.y) / 2,
});

/**
 * Ends this far outside a mezzanine still count as on it, e.g. snapped to the hall or office
 * wall the mezzanine stops short of.
 */
export const WALL_ON_MEZZANINE_REACH = 0.5;

/** The mezzanine a wall lies on with both ends, i.e. that it stands on or could stand on. */
export function wallMezzanine(doc: LevelDoc, w: Pick<Wall, 'start' | 'end'>): Mezzanine | null {
  const near = (p: Vec2) =>
    mezzanineAt(doc, p) ??
    mezzaninesOf(doc).find((m) => distanceToOutline(p, m.polygon) <= WALL_ON_MEZZANINE_REACH) ??
    null;
  const a = near(w.start);
  return a && near(w.end)?.id === a.id ? a : null;
}

/** A wall on a mezzanine reaches from its floor up to the eave of the hall. */
export const mezzanineWallHeight = (doc: LevelDoc, m: Mezzanine): number =>
  Math.max(0.5, (doc.buildings[m.buildingId]?.eaveHeight ?? m.elevation + 3) - m.elevation);

/** Absolute elevation of a wall's bottom edge. */
export const wallBase = (doc: LevelDoc, w: Wall): number =>
  levelElevation(doc, w.level, mid(w)) + w.baseElevation;

export interface OpeningFloor {
  /** Height of that floor above the wall's bottom edge. */
  readonly offset: number;
  /** Mezzanine or office storey it is, `null` when it is the floor the wall stands on. */
  readonly name: string | null;
}

/**
 * Floor a sill counts from: the highest one below it right in front of the opening, on either
 * side of the wall, e.g. a mezzanine or an office storey next to a hall wall that stands on
 * the hall floor.
 */
export function openingFloor(
  doc: LevelDoc,
  w: Wall,
  o: Pick<Opening, 'offsetFromWallStart' | 'width' | 'sillHeight'>,
): OpeningFloor {
  const base = wallBase(doc, w);
  const sill = base + o.sillHeight;
  const f = wallFrame(w);
  if (f.length <= 0) return { offset: 0, name: null };
  const along = o.offsetFromWallStart + o.width / 2;
  const c = { x: w.start.x + f.dir.x * along, y: w.start.y + f.dir.y * along };
  const reach = w.thickness / 2 + 0.3;
  const sides = [1, -1].map((k) => ({
    x: c.x + f.normal.x * reach * k,
    y: c.y + f.normal.y * reach * k,
  }));
  let best: { elevation: number; name: string | null } = { elevation: base, name: null };
  const consider = (elevation: number, name: string) => {
    if (elevation > best.elevation + 0.005 && elevation <= sill + 0.005) best = { elevation, name };
  };
  for (const p of sides) {
    for (const m of mezzaninesOf(doc))
      if (pointInPolygon(p, m.polygon)) consider(m.elevation, m.name);
    for (const b of Object.values(doc.buildings)) {
      if (!b.office) continue;
      const e = levelElevations(b.office);
      for (let k = 1; k <= b.office.floors.length; k++)
        if (pointInPolygon(p, officeLevelSlab(b, k)))
          consider(e[k] ?? 0, `${b.name} · ${levelLabel(k, b)}`);
    }
  }
  return { offset: best.elevation - base, name: best.name };
}

export interface WallFloor {
  /** Mezzanine or office storey, as in `OpeningFloor`. */
  readonly name: string;
  /** Height of that floor above the wall's bottom edge. */
  readonly offset: number;
  /** Part of the wall it lies along (distances from the wall start). */
  readonly from: number;
  readonly to: number;
}

/**
 * Raised floors right next to wall `w` (mezzanines, office storeys on either side), lowest
 * first, with the stretch of the wall each lies along: where a door or window for that floor
 * can go. The floor the wall stands on is not listed.
 */
export function wallFloors(doc: LevelDoc, w: Wall): WallFloor[] {
  const base = wallBase(doc, w);
  const f = wallFrame(w);
  if (f.length <= 0) return [];
  const reach = w.thickness / 2 + 0.3;
  const step = Math.min(0.25, f.length / 4);
  const found = new Map<string, { name: string; offset: number; from: number; to: number }>();
  const note = (key: string, name: string, elevation: number, along: number) => {
    const offset = elevation - base;
    // Only floors the wall reaches up to, above its own bottom.
    if (offset < 0.3 || offset > w.height - 0.3) return;
    const had = found.get(key);
    found.set(key, {
      name,
      offset,
      from: Math.min(had?.from ?? along, along),
      to: Math.max(had?.to ?? along, along),
    });
  };
  for (let along = step / 2; along < f.length; along += step) {
    const c = { x: w.start.x + f.dir.x * along, y: w.start.y + f.dir.y * along };
    for (const k of [1, -1]) {
      const p = { x: c.x + f.normal.x * reach * k, y: c.y + f.normal.y * reach * k };
      for (const m of mezzaninesOf(doc))
        if (pointInPolygon(p, m.polygon)) note(`m:${m.id}`, m.name, m.elevation, along);
      for (const b of Object.values(doc.buildings)) {
        if (!b.office) continue;
        const e = levelElevations(b.office);
        for (let lv = 1; lv <= b.office.floors.length; lv++)
          if (pointInPolygon(p, officeLevelSlab(b, lv)))
            note(`o:${b.id}:${String(lv)}`, `${b.name} · ${levelLabel(lv, b)}`, e[lv] ?? 0, along);
      }
    }
  }
  return [...found.values()]
    .map((x) => ({
      ...x,
      from: Math.max(0, x.from - step / 2),
      to: Math.min(f.length, x.to + step / 2),
    }))
    .sort((a, b) => a.offset - b.offset);
}

export interface ObjectGround {
  /** Terrain height under the object's middle (average of its front and back). */
  readonly height: number;
  /** Tilt along the depth axis in radians; positive = front higher than back. */
  readonly pitch: number;
}

/**
 * Terrain under a ground-floor object: in a depressed dock approach a truck stands lower, and
 * one reaching onto the ramp tilts along its length (front and back are sampled).
 */
export function objectGround(
  doc: Pick<ProjectDocument, 'openings' | 'walls'>,
  o: PlacedObject,
): ObjectGround {
  if (o.level > 0) return { height: 0, pitch: 0 };
  const half = rotate({ x: 0, y: o.dimensions.depth / 2 }, o.transform.rotation);
  const p = o.transform.position;
  // The front of an object is its local −y side.
  const front = groundElevation(doc, { x: p.x - half.x, y: p.y - half.y });
  const back = groundElevation(doc, { x: p.x + half.x, y: p.y + half.y });
  if (front === 0 && back === 0) return { height: 0, pitch: 0 };
  return { height: (front + back) / 2, pitch: Math.atan2(front - back, o.dimensions.depth) };
}

/** Absolute elevation of an object's bottom. */
export const objectBase = (
  doc: LevelDoc & Pick<ProjectDocument, 'openings' | 'walls'>,
  o: PlacedObject,
): number =>
  levelElevation(doc, o.level, o.transform.position) +
  objectGround(doc, o).height +
  o.transform.position.z;

const focusCache = new WeakMap<ProjectDocument, Map<string, ProjectDocument>>();

/**
 * The document reduced to one office building and everything in it (walls, openings, rooms,
 * zones, stairs, objects), for the office mode: the hall, site and outdoor facilities are
 * left out. Cached per document instance and office.
 */
export function focusDocument(doc: ProjectDocument, buildingId: string): ProjectDocument {
  let byId = focusCache.get(doc);
  if (!byId) {
    byId = new Map();
    focusCache.set(doc, byId);
  }
  const cached = byId.get(buildingId);
  if (cached) return cached;
  const b = doc.buildings[buildingId];
  if (!b) return doc;
  const fp = buildingCorners(b);
  const inside = (p: Vec2) => pointInOrOnPolygon(p, fp, 1e-6);
  const pick = <T extends { id: string }>(map: Record<string, T>, keep: (e: T) => boolean) =>
    Object.fromEntries(
      Object.values(map)
        .filter(keep)
        .map((e) => [e.id, e]),
    );
  const rooms = pick(doc.rooms, (r) => r.buildingId === buildingId);
  const walls = pick(
    doc.walls,
    (w) =>
      w.ownerId === buildingId ||
      (w.ownerId !== null && rooms[w.ownerId] !== undefined) ||
      (w.ownerId !== null && doc.stairs[w.ownerId]?.buildingId === buildingId) ||
      (w.ownerId === null && inside(w.start) && inside(w.end)),
  );
  const out: ProjectDocument = {
    ...doc,
    sites: {},
    buildings: { [buildingId]: b },
    walls,
    openings: pick(doc.openings, (o) => walls[o.wallId] !== undefined),
    zones: pick(doc.zones, (z) => z.buildingId === buildingId),
    rooms,
    objects: pick(doc.objects, (o) => inside(o.transform.position)),
    measurements: {},
    annotations: {},
    roofElements: {},
    roads: {},
    parkings: {},
    fences: {},
    gates: {},
    stairs: pick(doc.stairs, (s) => s.buildingId === buildingId),
    mezzanines: {},
    canopies: pick(doc.canopies, (c) => walls[c.wallId] !== undefined),
    wallImages: pick(doc.wallImages, (p) => walls[p.wallId] !== undefined),
    railings: pick(doc.railings, (r) => r.points.every(inside)),
    columns: pick(doc.columns, (c) => c.buildingId === buildingId),
    background: null,
  };
  byId.set(buildingId, out);
  return out;
}

/** Gross floor area (BGF) of all floors of an office. */
export const officeFloorArea = (b: Building): number =>
  b.office ? b.office.floors.reduce((s, _, k) => s + storeyArea(b, k), 0) : 0;

// ---------------------------------------------------------------------------------------
// Stairs

export interface StairSteps {
  readonly rise: number;
  readonly risers: number;
  readonly riserHeight: number;
  readonly going: number;
}

/** Chosen step division of a stair: own going and riser count instead of the step rule. */
export interface StepChoice {
  /** Going (tread depth) in metres. */
  readonly going?: number | undefined;
  /** Risers more (+) or fewer (−) than the step rule gives. */
  readonly riserDelta?: number | undefined;
  /** Straight stair split in the middle by a landing, going on in the same direction. */
  readonly midLanding?: boolean | undefined;
  /**
   * Least height of the intermediate landing's top, to walk underneath it; the first flight
   * then takes more risers, if the rise leaves enough for the second one.
   */
  readonly landingTop?: number | undefined;
  /** Platform off the top landing to the left or right of the walking direction. */
  readonly sideExit?: Stair['sideExit'];
}

/** Depth of the intermediate landing of a straight stair (at least its width). */
export const MID_LANDING_DEPTH = 1;

/** Clear height under the intermediate landing of an open hall stair. */
export const LANDING_HEADROOM = 2.5;

/** Fewest risers of the flight above an intermediate landing. */
export const MIN_FLIGHT_RISERS = 3;

/** Risers up to the intermediate landing: half of them, or enough to reach `landingTop`. */
function firstFlightRisers(n: number, h: number, landingTop: number | undefined): number {
  const half = Math.ceil(n / 2);
  if (!landingTop || h <= 0) return half;
  const need = Math.ceil(landingTop / h - 1e-9);
  return need > half && need <= n - MIN_FLIGHT_RISERS ? need : half;
}

/**
 * Riser count and going for a floor-to-floor height (step rule 2 h + g ≈ 63 cm); a chosen
 * going or riser count makes the stair shorter or flatter.
 */
export function stairSteps(rise: number, choice: StepChoice = {}): StairSteps {
  const standard = Math.max(
    2,
    Math.ceil(rise / STAIR_RULES.riserMax),
    Math.round(rise / STAIR_RULES.riserTarget),
  );
  const risers = Math.max(2, standard + Math.round(choice.riserDelta ?? 0));
  const riserHeight = rise / risers;
  const going =
    choice.going ??
    Math.min(Math.max(0.63 - 2 * riserHeight, STAIR_RULES.goingMin), STAIR_RULES.goingMax);
  return { rise, risers, riserHeight, going };
}

/** Axis-aligned box in the stair's local frame with the height of its top surface. */
export interface StairBlock {
  readonly x0: number;
  readonly x1: number;
  readonly y0: number;
  readonly y1: number;
  readonly top: number;
  readonly landing: boolean;
  /** Exit landing at the upper floor level (in front of the door). */
  readonly exit?: boolean;
  /** Side platform off the exit landing; its far end stays open (see `Stair.sideExit`). */
  readonly bridge?: 'x0' | 'x1' | 'y0' | 'y1';
}

export interface StairLayout extends StairSteps {
  /** Footprint size in the local frame (x across, y along the first flight). */
  readonly width: number;
  readonly depth: number;
  /** Steps and landings; each block is solid from the lower floor up to `top`. */
  readonly blocks: readonly StairBlock[];
  /** Walking line from the first step to the arrival, for the "up" arrow in the plan. */
  readonly walkLine: readonly Vec2[];
  /** Horizontal length of the longest flight. */
  readonly runLength: number;
}

function flight(
  x0: number,
  x1: number,
  yStart: number,
  dir: 1 | -1,
  treads: number,
  going: number,
  firstTop: number,
  riser: number,
): StairBlock[] {
  return Array.from({ length: treads }, (_, i) => {
    const a = yStart + dir * i * going;
    const b = a + dir * going;
    return {
      x0,
      x1,
      y0: Math.min(a, b),
      y1: Math.max(a, b),
      top: firstTop + i * riser,
      landing: false,
    };
  });
}

function xflight(
  xStart: number,
  dir: 1 | -1,
  y0: number,
  y1: number,
  treads: number,
  going: number,
  firstTop: number,
  riser: number,
): StairBlock[] {
  return Array.from({ length: treads }, (_, i) => {
    const a = xStart + dir * i * going;
    const b = a + dir * going;
    return {
      x0: Math.min(a, b),
      x1: Math.max(a, b),
      y0,
      y1,
      top: firstTop + i * riser,
      landing: false,
    };
  });
}

/**
 * Geometry of a straight, U (two flights, half landing) or L (quarter landing) stair for a
 * given floor-to-floor height. The last riser leads onto the upper floor, so a flight of n
 * risers has n − 1 treads.
 */
export function stairLayout(
  type: StairType,
  width: number,
  turn: 'left' | 'right',
  rise: number,
  landingDepth = 0,
  choice: StepChoice = {},
): StairLayout {
  const raw = flightsLayout(type, width, turn, rise, choice);
  const side = choice.sideExit;
  // A side platform as wide as the stair needs a landing at least that deep to start from.
  const exitLanding = side ? Math.max(landingDepth, width) : landingDepth;
  if (exitLanding <= 0) return raw;
  // The exit landing continues the last flight in walking direction at upper floor level.
  const steps = raw.blocks.filter((b) => !b.landing);
  const last = steps[steps.length - 1];
  const prev = steps[steps.length - 2];
  if (!last) return raw;
  const lc = { x: (last.x0 + last.x1) / 2, y: (last.y0 + last.y1) / 2 };
  const pc = prev
    ? { x: (prev.x0 + prev.x1) / 2, y: (prev.y0 + prev.y1) / 2 }
    : { x: lc.x, y: lc.y - 1 };
  const alongY = Math.abs(lc.y - pc.y) >= Math.abs(lc.x - pc.x);
  const dir = alongY ? Math.sign(lc.y - pc.y) || 1 : Math.sign(lc.x - pc.x) || 1;
  const landing: StairBlock = alongY
    ? dir > 0
      ? {
          x0: last.x0,
          x1: last.x1,
          y0: last.y1,
          y1: last.y1 + exitLanding,
          top: rise,
          landing: true,
          exit: true,
        }
      : {
          x0: last.x0,
          x1: last.x1,
          y0: last.y0 - exitLanding,
          y1: last.y0,
          top: rise,
          landing: true,
          exit: true,
        }
    : dir > 0
      ? {
          x0: last.x1,
          x1: last.x1 + exitLanding,
          y0: last.y0,
          y1: last.y1,
          top: rise,
          landing: true,
          exit: true,
        }
      : {
          x0: last.x0 - exitLanding,
          x1: last.x0,
          y0: last.y0,
          y1: last.y1,
          top: rise,
          landing: true,
          exit: true,
        };
  const blocks = [...raw.blocks, landing];
  // Keep the footprint centered on the stair position; a side platform does not count, so
  // the stair stays where it is however long the platform gets.
  const x0 = Math.min(...blocks.map((b) => b.x0));
  const x1 = Math.max(...blocks.map((b) => b.x1));
  const y0 = Math.min(...blocks.map((b) => b.y0));
  const y1 = Math.max(...blocks.map((b) => b.y1));
  const cx = (x0 + x1) / 2;
  const cy = (y0 + y1) / 2;
  const walkEnd = [{ x: (landing.x0 + landing.x1) / 2, y: (landing.y0 + landing.y1) / 2 }];
  if (side) {
    // Left of the walking direction is the walking direction turned a quarter anticlockwise.
    const walk = alongY ? { x: 0, y: dir } : { x: dir, y: 0 };
    const k = side.side === 'left' ? 1 : -1;
    const out = { x: -walk.y * k, y: walk.x * k };
    const L = side.length;
    const bridge: StairBlock =
      out.x !== 0
        ? {
            x0: out.x < 0 ? landing.x0 - L : landing.x1,
            x1: out.x < 0 ? landing.x0 : landing.x1 + L,
            y0: landing.y0,
            y1: landing.y1,
            top: rise,
            landing: true,
            exit: true,
            bridge: out.x < 0 ? 'x0' : 'x1',
          }
        : {
            x0: landing.x0,
            x1: landing.x1,
            y0: out.y < 0 ? landing.y0 - L : landing.y1,
            y1: out.y < 0 ? landing.y0 : landing.y1 + L,
            top: rise,
            landing: true,
            exit: true,
            bridge: out.y < 0 ? 'y0' : 'y1',
          };
    blocks.push(bridge);
    // The walking line turns on the landing and ends at the open end of the platform.
    const mid = { x: (bridge.x0 + bridge.x1) / 2, y: (bridge.y0 + bridge.y1) / 2 };
    walkEnd.push(
      out.x !== 0
        ? { x: out.x < 0 ? bridge.x0 + 0.3 : bridge.x1 - 0.3, y: mid.y }
        : { x: mid.x, y: out.y < 0 ? bridge.y0 + 0.3 : bridge.y1 - 0.3 },
    );
  }
  return {
    ...raw,
    width: x1 - x0,
    depth: y1 - y0,
    blocks: blocks.map((b) => ({
      ...b,
      x0: b.x0 - cx,
      x1: b.x1 - cx,
      y0: b.y0 - cy,
      y1: b.y1 - cy,
    })),
    walkLine: [...raw.walkLine, ...walkEnd].map((p) => ({ x: p.x - cx, y: p.y - cy })),
  };
}

function flightsLayout(
  type: StairType,
  width: number,
  turn: 'left' | 'right',
  rise: number,
  choice: StepChoice,
): StairLayout {
  const s = stairSteps(rise, choice);
  const { risers: n, riserHeight: h, going: g } = s;
  const m = turn === 'left' ? -1 : 1;
  if (type === 'straight' && choice.midLanding && n >= 4) {
    // Two flights in line with a landing between them at about half the height.
    const n1 = firstFlightRisers(n, h, choice.landingTop);
    const t1 = n1 - 1;
    const t2 = n - n1 - 1;
    const landing = Math.max(MID_LANDING_DEPTH, width);
    const depth = (t1 + t2) * g + landing;
    const y0 = -depth / 2;
    const ly0 = y0 + t1 * g;
    const blocks = [
      ...flight(-width / 2, width / 2, y0, 1, t1, g, h, h),
      { x0: -width / 2, x1: width / 2, y0: ly0, y1: ly0 + landing, top: n1 * h, landing: true },
      ...flight(-width / 2, width / 2, ly0 + landing, 1, t2, g, (n1 + 1) * h, h),
    ];
    return {
      ...s,
      width,
      depth,
      blocks,
      runLength: Math.max(t1, t2) * g,
      walkLine: [
        { x: 0, y: -depth / 2 },
        { x: 0, y: depth / 2 },
      ],
    };
  }
  if (type === 'straight') {
    const depth = (n - 1) * g;
    const blocks = flight(-width / 2, width / 2, -depth / 2, 1, n - 1, g, h, h);
    return {
      ...s,
      width,
      depth,
      blocks,
      runLength: depth,
      walkLine: [
        { x: 0, y: -depth / 2 },
        { x: 0, y: depth / 2 },
      ],
    };
  }
  const n1 = firstFlightRisers(n, h, choice.landingTop);
  const n2 = n - n1;
  const t1 = n1 - 1;
  const t2 = n2 - 1;
  const landingTop = n1 * h;
  if (type === 'u') {
    const run = Math.max(t1, t2) * g;
    const W = 2 * width + STAIR_EYE;
    const D = run + width;
    const landingY0 = D / 2 - width;
    // Flight 1 on one side walking +y, landing across the full width, flight 2 back on the other side.
    const f1x = m > 0 ? [-W / 2, -W / 2 + width] : [W / 2 - width, W / 2];
    const f2x = m > 0 ? [W / 2 - width, W / 2] : [-W / 2, -W / 2 + width];
    const blocks = [
      ...flight(f1x[0] ?? 0, f1x[1] ?? 0, landingY0 - t1 * g, 1, t1, g, h, h),
      { x0: -W / 2, x1: W / 2, y0: landingY0, y1: D / 2, top: landingTop, landing: true },
      ...flight(f2x[0] ?? 0, f2x[1] ?? 0, landingY0, -1, t2, g, landingTop + h, h),
    ];
    const c1 = ((f1x[0] ?? 0) + (f1x[1] ?? 0)) / 2;
    const c2 = ((f2x[0] ?? 0) + (f2x[1] ?? 0)) / 2;
    const ly = D / 2 - width / 2;
    return {
      ...s,
      width: W,
      depth: D,
      blocks,
      runLength: run,
      walkLine: [
        { x: c1, y: landingY0 - t1 * g },
        { x: c1, y: ly },
        { x: c2, y: ly },
        { x: c2, y: landingY0 - t2 * g },
      ],
    };
  }
  // L: flight 1 along +y, square landing in the corner, flight 2 turning sideways.
  const W = width + t2 * g;
  const D = t1 * g + width;
  const lx0 = m > 0 ? -W / 2 : W / 2 - width;
  const landing = {
    x0: lx0,
    x1: lx0 + width,
    y0: D / 2 - width,
    y1: D / 2,
    top: landingTop,
    landing: true,
  };
  const blocks = [
    ...flight(lx0, lx0 + width, -D / 2, 1, t1, g, h, h),
    landing,
    ...xflight(
      m > 0 ? lx0 + width : lx0,
      m > 0 ? 1 : -1,
      D / 2 - width,
      D / 2,
      t2,
      g,
      landingTop + h,
      h,
    ),
  ];
  const cx = lx0 + width / 2;
  const cy = D / 2 - width / 2;
  return {
    ...s,
    width: W,
    depth: D,
    blocks,
    runLength: Math.max(t1, t2) * g,
    walkLine: [
      { x: cx, y: -D / 2 },
      { x: cx, y: cy },
      { x: m > 0 ? W / 2 : -W / 2, y: cy },
    ],
  };
}

/**
 * The mezzanine a hall stair leads up to: the one it was set to (where its top end meets the
 * edge), otherwise the one of its hall nearest to the stair.
 */
export function stairMezzanine(
  doc: LevelDoc,
  s: Pick<Stair, 'buildingId' | 'center'> & Partial<Pick<Stair, 'mezzanineId'>>,
): Mezzanine | null {
  const own = s.mezzanineId ? doc.mezzanines?.[s.mezzanineId] : undefined;
  if (own && own.buildingId === s.buildingId) return own;
  let best: { m: Mezzanine; d: number } | null = null;
  for (const m of mezzaninesOf(doc)) {
    if (m.buildingId !== s.buildingId) continue;
    const d = Math.min(
      ...m.polygon.map((p, i) =>
        distancePointToSegment(s.center, { a: p, b: m.polygon[(i + 1) % m.polygon.length] ?? p }),
      ),
    );
    if (!best || d < best.d) best = { m, d };
  }
  return best?.m ?? null;
}

/** Least height a stair climbs (two risers), e.g. when it starts just below its mezzanine. */
export const MIN_STAIR_RISE = 0.3;

/** How close the top end of a hall stair has to come to a mezzanine edge to lead onto it. */
export const STAIR_TARGET_REACH = 1;

const distanceToOutline = (p: Vec2, poly: readonly Vec2[]): number =>
  Math.min(
    ...poly.map((a, i) => distancePointToSegment(p, { a, b: poly[(i + 1) % poly.length] ?? a })),
  );

/** A floor a hall stair leads up to: one of its mezzanines or a floor of an office in it. */
export type HallStairTarget =
  | { readonly kind: 'mezzanine'; readonly mezzanineId: string; readonly elevation: number }
  | {
      readonly kind: 'office';
      readonly officeId: string;
      readonly level: number;
      readonly elevation: number;
    }
  | RoofStairTarget;

/** A flat roof a stair leads out onto (roof terrace). */
export interface RoofStairTarget {
  readonly kind: 'roof';
  readonly buildingId: string;
  readonly elevation: number;
}

/** Offices always have a flat roof; halls only with roof type `flat`. */
export const hasFlatRoof = (b: Building): boolean => b.kind === 'office' || b.roofType === 'flat';

/** Walking height on a flat roof (on top of the roof build-up; an indoor office's top slab). */
export const roofTop = (b: Building): number =>
  b.office?.placement === 'indoor' ? b.eaveHeight : b.eaveHeight + FLAT_ROOF_THICKNESS;

/**
 * Flat roofs over a stair it can lead out onto: that of its own free-standing office or hall,
 * or of the hall around it (an office built into a hall has its top slab as a floor instead).
 */
export function stairRoofTargets(
  doc: LevelDoc,
  s: Pick<Stair, 'center' | 'buildingId' | 'fromLevel' | 'bottom'>,
): RoofStairTarget[] {
  const own = doc.buildings[s.buildingId];
  const base = own?.office ? (levelElevations(own.office)[s.fromLevel] ?? 0) : (s.bottom ?? 0);
  return Object.values(doc.buildings)
    .filter(
      (b) =>
        hasFlatRoof(b) &&
        b.office?.placement !== 'indoor' &&
        (b.id === s.buildingId || pointInPolygon(s.center, buildingCorners(b))) &&
        roofTop(b) - base >= MIN_STAIR_RISE,
    )
    .map((b) => ({ kind: 'roof' as const, buildingId: b.id, elevation: roofTop(b) }))
    .sort((p, q) => p.elevation - q.elevation);
}

/** The roof a stair was set to lead onto, while it is still flat. */
export function stairRoof(doc: LevelDoc, s: Pick<Stair, 'toRoof'>): RoofStairTarget | null {
  const b = s.toRoof ? doc.buildings[s.toRoof] : undefined;
  return b && hasFlatRoof(b) && b.office?.placement !== 'indoor'
    ? { kind: 'roof', buildingId: b.id, elevation: roofTop(b) }
    : null;
}

/** The stair leading to `t` instead (the other kinds of target dropped). */
export function stairWithTarget(s: Stair, t: HallStairTarget): Stair {
  const { mezzanineId: _mezzanine, toOffice: _office, toRoof: _roof, ...rest } = s;
  return t.kind === 'mezzanine'
    ? { ...rest, mezzanineId: t.mezzanineId }
    : t.kind === 'roof'
      ? { ...rest, toRoof: t.buildingId }
      : { ...rest, toOffice: { officeId: t.officeId, level: t.level } };
}

/**
 * Where stairs come up through the flat roof of `b`, as roof openings in its local frame (the
 * stair's whole footprint).
 */
export function stairRoofOpenings(
  doc: LevelDoc & Pick<ProjectDocument, 'stairs'>,
  b: Building,
): RoofOpening[] {
  return Object.values(doc.stairs)
    .filter((s) => s.toRoof === b.id && stairRoof(doc, s))
    .map((s) => {
      const l = stairGeometry(doc, s);
      return {
        center: rotate({ x: s.center.x - b.center.x, y: s.center.y - b.center.y }, -b.rotation),
        width: l.width,
        depth: l.depth,
        rotation: s.rotation - b.rotation,
      };
    });
}

/** Every floor a stair in `hallId` can lead up to, with the outline of its edge. */
export function hallStairTargets(
  doc: LevelDoc,
  hallId: string,
): (HallStairTarget & { readonly outline: readonly Vec2[] })[] {
  const hall = doc.buildings[hallId];
  if (hall?.kind !== 'hall') return [];
  const out: (HallStairTarget & { readonly outline: readonly Vec2[] })[] = mezzaninesOf(doc)
    .filter((m) => m.buildingId === hallId)
    .map((m) => ({
      kind: 'mezzanine' as const,
      mezzanineId: m.id,
      elevation: m.elevation,
      outline: m.polygon,
    }));
  for (const f of hallOfficeFloors(doc, hall)) out.push({ kind: 'office', ...f });
  return out;
}

/** The floor a hall stair leads up to: an office floor it was set to, else its mezzanine. */
export function stairTop(doc: LevelDoc, s: Stair): HallStairTarget | null {
  const roof = stairRoof(doc, s);
  if (roof) return roof;
  const o = s.toOffice ? doc.buildings[s.toOffice.officeId] : undefined;
  if (s.toOffice && o?.office && s.toOffice.level <= o.office.floors.length)
    return {
      kind: 'office',
      officeId: o.id,
      level: s.toOffice.level,
      elevation: levelElevations(o.office)[s.toOffice.level] ?? 0,
    };
  const m = stairMezzanine(doc, s);
  return m ? { kind: 'mezzanine', mezzanineId: m.id, elevation: m.elevation } : null;
}

/**
 * The floor whose edge the top end of a hall stair reaches (within `STAIR_TARGET_REACH`):
 * mezzanines and office floors, the stair laid out for each candidate's height; null if it
 * reaches none.
 */
export function stairTargetAt(doc: LevelDoc, s: Stair): HallStairTarget | null {
  let best: { t: HallStairTarget; d: number } | null = null;
  for (const t of hallStairTargets(doc, s.buildingId)) {
    if (t.elevation - (s.bottom ?? 0) < MIN_STAIR_RISE) continue;
    const line = stairGeometry(doc, stairWithTarget(s, t)).walkLine;
    const end = line[line.length - 1];
    if (!end) continue;
    const d = distanceToOutline(stairToWorld(s, end), t.outline);
    if (d <= STAIR_TARGET_REACH && (!best || d < best.d)) best = { t, d };
  }
  return best?.t ?? null;
}

/**
 * Floor-to-floor height a stair has to climb: from its office's floor data, or for a hall
 * stair from where it starts (hall floor, an office floor, a lower mezzanine) up to the floor
 * it leads to.
 */
export function stairRise(doc: LevelDoc, s: Stair): number {
  if (s.rise !== undefined) return Math.max(MIN_STAIR_RISE, s.rise);
  const roof = stairRoof(doc, s);
  if (roof) return Math.max(MIN_STAIR_RISE, roof.elevation - stairBase(doc, s));
  const b = doc.buildings[s.buildingId];
  if (b?.kind === 'hall')
    return Math.max(MIN_STAIR_RISE, (stairTop(doc, s)?.elevation ?? 3) - (s.bottom ?? 0));
  if (!b?.office) return 3;
  const e = levelElevations(b.office);
  return (e[s.fromLevel + 1] ?? e[e.length - 1] ?? 3) - (e[s.fromLevel] ?? 0);
}

export function stairGeometry(doc: LevelDoc, s: Stair): StairLayout {
  return stairLayout(s.type, s.width, s.turn, stairRise(doc, s), s.exitLanding, {
    going: s.going,
    riserDelta: s.riserDelta,
    midLanding: s.midLanding,
    landingTop: landingTopFor(s),
    sideExit: s.sideExit,
  });
}

/**
 * Length a side platform to `side` needs to reach the nearest wall face at its height (e.g.
 * across the columns and girts of a hall wall), or null when no wall is within 20 m. Walls of
 * the stair's own stairwell do not count.
 */
export function sideExitReach(
  doc: LevelDoc & Pick<ProjectDocument, 'walls'>,
  s: Stair,
  side: 'left' | 'right',
): number | null {
  const probe = { ...s, sideExit: { side, length: 1 } };
  const b = stairGeometry(doc, probe).blocks.find((x) => x.bridge);
  if (!b?.bridge) return null;
  // Middle of the edge the platform starts from, and the direction it runs in (local frame).
  const local =
    b.bridge === 'x0'
      ? { from: { x: b.x1, y: (b.y0 + b.y1) / 2 }, dir: { x: -1, y: 0 } }
      : b.bridge === 'x1'
        ? { from: { x: b.x0, y: (b.y0 + b.y1) / 2 }, dir: { x: 1, y: 0 } }
        : b.bridge === 'y0'
          ? { from: { x: (b.x0 + b.x1) / 2, y: b.y1 }, dir: { x: 0, y: -1 } }
          : { from: { x: (b.x0 + b.x1) / 2, y: b.y0 }, dir: { x: 0, y: 1 } };
  const from = stairToWorld(s, local.from);
  const ahead = stairToWorld(s, { x: local.from.x + local.dir.x, y: local.from.y + local.dir.y });
  const dir = { x: ahead.x - from.x, y: ahead.y - from.y };
  const top = stairBase(doc, s) + stairRise(doc, s);
  let best: number | null = null;
  for (const w of Object.values(doc.walls)) {
    if (w.ownerId === s.id) continue;
    const z = wallBase(doc, w);
    if (top < z - 0.05 || top > z + w.height + 0.05) continue;
    const f = wallFrame(w);
    const cross = dir.x * f.dir.y - dir.y * f.dir.x;
    if (Math.abs(cross) < 0.2) continue;
    // Ray `from + t·dir` meets the wall's centre line at `w.start + u·f.dir`.
    const rx = w.start.x - from.x;
    const ry = w.start.y - from.y;
    const t = (rx * f.dir.y - ry * f.dir.x) / cross;
    const u = (rx * dir.y - ry * dir.x) / cross;
    if (u < -0.05 || u > f.length + 0.05) continue;
    const face = t - w.thickness / 2 / Math.abs(cross);
    if (face > 0.05 && face <= 20 && (best === null || face < best)) best = face;
  }
  return best === null ? null : Math.max(0.2, Math.round(best * 100) / 100);
}

/** Outline of the side platform at the top (world coordinates), or null without one. */
export function stairSideExit(doc: LevelDoc, s: Stair): Vec2[] | null {
  const b = stairGeometry(doc, s).blocks.find((x) => x.bridge);
  if (!b) return null;
  return [
    { x: b.x0, y: b.y0 },
    { x: b.x1, y: b.y0 },
    { x: b.x1, y: b.y1 },
    { x: b.x0, y: b.y1 },
  ].map((p) => stairToWorld(s, p));
}

/** Intermediate landings leave headroom underneath, below the plate of the landing. */
export const landingTopFor = (s: Pick<Stair, 'construction'>): number =>
  LANDING_HEADROOM + (s.construction === 'steel' ? STEEL_LANDING : CONCRETE_LANDING);

/** Absolute elevation of the stair's lower floor. */
export function stairBase(doc: Pick<ProjectDocument, 'buildings'>, s: Stair): number {
  const b = doc.buildings[s.buildingId];
  return b?.office ? (levelElevations(b.office)[s.fromLevel] ?? 0) : (s.bottom ?? 0);
}

export interface StairStartFloor {
  readonly officeId: string | null;
  /** A lower mezzanine the stair starts on (e.g. from one stage up to a higher one). */
  readonly mezzanineId?: string;
  readonly level: number;
  readonly elevation: number;
  /** Where that floor is, to tell which one the foot of the stair stands on. */
  readonly outline: readonly Vec2[];
}

/**
 * Upper floors of the offices touching `hall` (also partly outside it) with their slab
 * outline: every storey above the ground floor and the top slab of an office built in.
 */
function hallOfficeFloors(
  doc: LevelDoc,
  hall: Building,
): { officeId: string; level: number; elevation: number; outline: Vec2[] }[] {
  const footprint = buildingCorners(hall);
  return Object.values(doc.buildings).flatMap((b) => {
    const o = b.office;
    if (!o || b.id === hall.id) return [];
    const corners = buildingCorners(b);
    const touches =
      pointInPolygon(b.center, footprint) ||
      corners.some((p) => pointInPolygon(p, footprint)) ||
      footprint.some((p) => pointInPolygon(p, corners));
    if (!touches) return [];
    const e = levelElevations(o);
    const top = o.placement === 'indoor' ? o.floors.length : o.floors.length - 1;
    return Array.from({ length: top }, (_, i) => ({
      officeId: b.id,
      level: i + 1,
      elevation: e[i + 1] ?? 0,
      outline: officeLevelSlab(b, i + 1),
    }));
  });
}

/**
 * Floors a hall stair up to `target` can start on: the hall floor, every other mezzanine of
 * the hall and every office floor (or office top) touching it, all lower than the target.
 */
export function stairStartFloors(
  doc: LevelDoc,
  s: Pick<Stair, 'buildingId'>,
  target: Pick<Mezzanine, 'elevation'> & Partial<Pick<Mezzanine, 'id'>>,
): StairStartFloor[] {
  const hall = doc.buildings[s.buildingId];
  if (!hall) return [];
  const below = (h: number) => h < target.elevation - MIN_STAIR_RISE;
  const out: StairStartFloor[] = [
    { officeId: null, level: 0, elevation: 0, outline: buildingCorners(hall) },
  ];
  for (const m of mezzaninesOf(doc))
    if (m.buildingId === s.buildingId && m.id !== target.id && below(m.elevation))
      out.push({
        officeId: null,
        mezzanineId: m.id,
        level: 1,
        elevation: m.elevation,
        outline: m.polygon,
      });
  for (const f of hallOfficeFloors(doc, hall)) if (below(f.elevation)) out.push(f);
  return out;
}

/**
 * Absolute elevation of what a stair stands on: its own floor inside the office, the ground
 * (0) outside it, e.g. for the upper flights of an outside stair over several floors.
 */
export function stairGround(doc: Pick<ProjectDocument, 'buildings'>, s: Stair): number {
  return stairOutside(doc, s) ? 0 : stairBase(doc, s);
}

/** Whether the stair stands outside its office (beside it), not on one of its floors. */
export function stairOutside(doc: Pick<ProjectDocument, 'buildings'>, s: Stair): boolean {
  const b = doc.buildings[s.buildingId];
  if (!b?.office) return false;
  return !pointInPolygon(s.center, storeyCorners(b, s.fromLevel));
}

/**
 * The stair of the same stairwell one floor below (`-1`) or above (`1`) that a straight flight
 * continues from or into, in line; null for other stairs.
 */
export function stairInLine(
  doc: Pick<ProjectDocument, 'stairs'>,
  s: Stair,
  side: -1 | 1,
): Stair | null {
  if (s.type !== 'straight' || s.groupId === undefined) return null;
  return (
    Object.values(doc.stairs).find(
      (x) => x.groupId === s.groupId && x.fromLevel === s.fromLevel + side,
    ) ?? null
  );
}

export const stairToWorld = (s: Pick<Stair, 'center' | 'rotation'>, p: Vec2): Vec2 =>
  localToWorld(p, { position: s.center, rotation: s.rotation });

/** Grating steps of a steel stair. */
export const STEEL_TREAD = 0.05;
/** Landing plates of a steel stair. */
export const STEEL_LANDING = 0.1;
/** Square posts under the landings of a steel stair. */
export const STEEL_POST = 0.1;

/** Concrete waist under the steps of a slab stair, and its landing slabs. */
export const CONCRETE_WAIST = 0.16;
export const CONCRETE_LANDING = 0.2;

/**
 * Thickness of a step or landing: solid stairs reach down to the floor; a concrete slab
 * stair has its steps on an inclined waist and landing slabs, open underneath like a steel
 * stair (`riser` = height of one step).
 */
export const stairBlockThickness = (
  s: Pick<Stair, 'construction'>,
  b: StairBlock,
  riser = 0.18,
  /** How far the ground lies below the stair's floor (a solid stair reaches down to it). */
  drop = 0,
): number => {
  // The side platform is a plate spanning over to the building, whatever the stair is made of.
  if (b.bridge)
    return Math.min(b.top, s.construction === 'steel' ? STEEL_LANDING : CONCRETE_LANDING);
  if (s.construction === 'steel') return Math.min(b.top, b.landing ? STEEL_LANDING : STEEL_TREAD);
  if (s.construction === 'slab')
    return Math.min(b.top, b.landing ? CONCRETE_LANDING : riser + CONCRETE_WAIST);
  return b.top + drop;
};

/** Square concrete columns under the landings of a slab stair. */
export const CONCRETE_POST = 0.25;

/** Depth of the landing in front of the first step inside an enclosed stairwell. */
export const stairEntry = (s: Pick<Stair, 'enclosed' | 'entry'>): number =>
  s.enclosed ? (s.entry ?? STAIRWELL_ENTRY) : 0;

/**
 * Posts under the landings of a steel stair (stair frame): at the landing corners away from
 * the building wall, i.e. at all corners; landings lower than half a metre need none.
 */
export function steelStairPosts(
  l: StairLayout,
  /** How far the ground lies below the stair's floor: the posts reach down to it. */
  drop = 0,
  post = STEEL_POST,
  plate = STEEL_LANDING,
): { at: Vec2; height: number }[] {
  const inset = post / 2 + 0.03;
  // The side platform spans from the landing to the building and needs no posts of its own.
  return l.blocks
    .filter((b) => b.landing && !b.bridge && b.top + drop > 0.5)
    .flatMap((b) =>
      [
        { x: b.x0 + inset, y: b.y0 + inset },
        { x: b.x1 - inset, y: b.y0 + inset },
        { x: b.x1 - inset, y: b.y1 - inset },
        { x: b.x0 + inset, y: b.y1 - inset },
      ].map((at) => ({ at, height: b.top - plate + drop })),
    );
}

/**
 * Supports under the landings: steel posts of a steel stair, and outside the building concrete
 * columns of a slab stair (inside its landings rest on walls; a solid stair reaches down by
 * itself). Heights start at the ground.
 */
export function stairLandingPosts(
  l: StairLayout,
  s: Pick<Stair, 'construction'>,
  drop = 0,
  outside = false,
): { at: Vec2; height: number; size: number }[] {
  if (s.construction === 'steel')
    return steelStairPosts(l, drop).map((p) => ({ ...p, size: STEEL_POST }));
  if (s.construction === 'slab' && outside)
    return steelStairPosts(l, drop, CONCRETE_POST, CONCRETE_LANDING).map((p) => ({
      ...p,
      size: CONCRETE_POST,
    }));
  return [];
}

/**
 * Footprint polygon in world coordinates (counter-clockwise); with `outer` an enclosed
 * stairwell includes its walls and entry landing.
 */
export function stairFootprint(doc: LevelDoc, s: Stair, outer = true): Vec2[] {
  const l = stairGeometry(doc, s);
  const t = outer && s.enclosed ? STAIRWELL_WALL : 0;
  const entry = outer ? stairEntry(s) : 0;
  return [
    { x: -l.width / 2 - t, y: -l.depth / 2 - entry - t },
    { x: l.width / 2 + t, y: -l.depth / 2 - entry - t },
    { x: l.width / 2 + t, y: l.depth / 2 + t },
    { x: -l.width / 2 - t, y: l.depth / 2 + t },
  ].map((p) => stairToWorld(s, p));
}

/**
 * Opening the stair needs in the slab of the floor it leads up to (world coordinates): its
 * footprint without the room beyond the last step. There the last riser leads onto the slab,
 * e.g. next to the shorter second flight of a U stair with an odd number of risers.
 */
export function stairOpening(doc: LevelDoc, s: Stair): Vec2[] {
  const l = stairGeometry(doc, s);
  const [w, d] = [l.width / 2, l.depth / 2];
  const rect = [
    { x: -w, y: -d },
    { x: w, y: -d },
    { x: w, y: d },
    { x: -w, y: d },
  ];
  const world = (poly: readonly Vec2[]) => poly.map((p) => stairToWorld(s, p));
  const steps = l.blocks.filter((b) => !b.landing);
  const last = steps[steps.length - 1];
  const a = l.walkLine[l.walkLine.length - 2];
  const b = l.walkLine[l.walkLine.length - 1];
  if (!last || !a || !b || l.blocks.some((x) => x.exit)) return world(rect);
  const alongY = Math.abs(b.y - a.y) >= Math.abs(b.x - a.x);
  const ahead = alongY ? Math.sign(b.y - a.y) : Math.sign(b.x - a.x);
  const [from, to] = alongY
    ? ahead > 0
      ? [last.y1, d]
      : [-d, last.y0]
    : ahead > 0
      ? [last.x1, w]
      : [-w, last.x0];
  if (to - from < 1e-3) return world(rect);
  const strip = alongY
    ? [
        { x: last.x0, y: from },
        { x: last.x1, y: from },
        { x: last.x1, y: to },
        { x: last.x0, y: to },
      ]
    : [
        { x: from, y: last.y0 },
        { x: to, y: last.y0 },
        { x: to, y: last.y1 },
        { x: from, y: last.y1 },
      ];
  const rest = subtractPolygons(rect, [strip]).sort(
    (p, q) => polygonArea(q.outer) - polygonArea(p.outer),
  )[0];
  return world(rest?.outer ?? rect);
}

/** Handrail height above the step nosing and guard height at landings. */
export const HANDRAIL_HEIGHT = 0.9;
export const STAIR_GUARD_HEIGHT = 1;

export interface StairRail {
  /** World coordinates; the rail is straight (sloped along flights, level at landings). */
  readonly a: Vec2;
  readonly b: Vec2;
  /** Absolute heights of the rail top at `a` and `b`. */
  readonly za: number;
  readonly zb: number;
  /** Guard (with posts and knee rail) on open sides, handrail only along walls. */
  readonly kind: 'guard' | 'handrail';
}

const near = (a: number, b: number) => Math.abs(a - b) < 1e-6;

/** Sides a stair needs protection on: open flight sides and landing edges, never the entry. */
/**
 * Whether one can walk on at height `z` at point `p`: a mezzanine, an office storey above the
 * ground floor or the usable top of an office built into a hall.
 */
function walkableAt(
  doc: LevelDoc & Partial<Pick<ProjectDocument, 'mezzanines'>>,
  p: Vec2,
  z: number,
): boolean {
  const near = (h: number) => Math.abs(h - z) < 0.15;
  if (
    Object.values(doc.mezzanines ?? {}).some(
      (m) => near(m.elevation) && pointInOrOnPolygon(p, m.polygon, 0.05),
    )
  )
    return true;
  return Object.values(doc.buildings).some((b) => {
    const o = b.office;
    if (!o) return false;
    const e = levelElevations(o);
    return Array.from({ length: topLevel(b) }, (_, i) => i + 1).some(
      (k) =>
        near(e[k] ?? -1) &&
        pointInOrOnPolygon(p, storeyCorners(b, Math.min(k, o.floors.length - 1)), 0.05),
    );
  });
}

export function stairRailings(
  doc: LevelDoc &
    Pick<ProjectDocument, 'walls'> &
    Partial<Pick<ProjectDocument, 'stairs' | 'mezzanines'>>,
  s: Stair,
): StairRail[] {
  const l = stairGeometry(doc, s);
  // Where the next flight goes on in line, the far edge of the exit landing stays open.
  const goesOn = doc.stairs !== undefined && stairInLine({ stairs: doc.stairs }, s, 1) !== null;
  const farEnd = Math.max(...l.blocks.map((b) => b.y1));
  const base = stairBase(doc, s);
  const world = (p: Vec2) => stairToWorld(s, p);
  const walls = Object.values(doc.walls);
  const againstWall = (a: Vec2, b: Vec2): boolean => {
    // Stairwell walls are real walls, found like any other wall next to the stair.
    const wa = world(a);
    const wb = world(b);
    const len = Math.hypot(wb.x - wa.x, wb.y - wa.y);
    if (len < 1e-6) return false;
    const dir = { x: (wb.x - wa.x) / len, y: (wb.y - wa.y) / len };
    const probes = [0.15, 0.5, 0.85].map((t) => ({
      x: wa.x + (wb.x - wa.x) * t,
      y: wa.y + (wb.y - wa.y) * t,
    }));
    return walls.some((w) => {
      const f = wallFrame(w);
      if (Math.abs(f.dir.x * dir.x + f.dir.y * dir.y) < 0.95) return false;
      const tol = w.thickness / 2 + 0.2;
      return probes.every((p) => distancePointToSegment(p, { a: w.start, b: w.end }) <= tol);
    });
  };
  const rail = (a: Vec2, b: Vec2, za: number, zb: number, kind: StairRail['kind']): StairRail => ({
    a: world(a),
    b: world(b),
    za: base + za,
    zb: base + zb,
    kind,
  });
  const out: StairRail[] = [];
  const steps = l.blocks.filter((b) => !b.landing);
  // Flights: consecutive steps between landings.
  const runs: StairBlock[][] = [];
  let run: StairBlock[] = [];
  for (const b of l.blocks) {
    if (b.landing) {
      if (run.length > 0) runs.push(run);
      run = [];
    } else run.push(b);
  }
  if (run.length > 0) runs.push(run);
  for (const r of runs) {
    const first = r[0];
    const last = r[r.length - 1];
    if (!first || !last) continue;
    const alongY = first.y1 - first.y0 <= first.x1 - first.x0;
    const up = alongY ? last.y0 >= first.y0 : last.x0 >= first.x0;
    const inset = 0.04;
    const sides = alongY
      ? [first.x0 + inset, first.x1 - inset]
      : [first.y0 + inset, first.y1 - inset];
    const start = alongY ? (up ? first.y0 : first.y1) : up ? first.x0 : first.x1;
    const end = alongY ? (up ? last.y1 : last.y0) : up ? last.x1 : last.x0;
    for (const c of sides) {
      const a = alongY ? { x: c, y: start } : { x: start, y: c };
      const b = alongY ? { x: c, y: end } : { x: end, y: c };
      const edgeA = alongY
        ? { x: c > (first.x0 + first.x1) / 2 ? first.x1 : first.x0, y: start }
        : { x: start, y: c > (first.y0 + first.y1) / 2 ? first.y1 : first.y0 };
      const edgeB = alongY ? { x: edgeA.x, y: end } : { x: end, y: edgeA.y };
      out.push(
        rail(
          a,
          b,
          first.top + HANDRAIL_HEIGHT,
          last.top + HANDRAIL_HEIGHT,
          againstWall(edgeA, edgeB) ? 'handrail' : 'guard',
        ),
      );
    }
  }
  // Landings: every edge that is neither shared with a flight nor against a wall.
  const sharesEdge = (x0: number, y0: number, x1: number, y1: number) =>
    steps.some((st) =>
      near(y0, y1)
        ? (near(st.y0, y0) || near(st.y1, y0)) && Math.min(x1, st.x1) - Math.max(x0, st.x0) > 1e-3
        : (near(st.x0, x0) || near(st.x1, x0)) && Math.min(y1, st.y1) - Math.max(y0, st.y0) > 1e-3,
    );
  const landings = l.blocks.filter((x) => x.landing);
  // The exit landing and its side platform meet without a railing between them.
  const sharesLanding = (b: StairBlock, p: Vec2, q: Vec2) =>
    landings.some(
      (o) =>
        o !== b &&
        near(o.top, b.top) &&
        (near(p.y, q.y)
          ? (near(o.y0, p.y) || near(o.y1, p.y)) &&
            Math.min(Math.max(p.x, q.x), o.x1) - Math.max(Math.min(p.x, q.x), o.x0) > 1e-3
          : (near(o.x0, p.x) || near(o.x1, p.x)) &&
            Math.min(Math.max(p.y, q.y), o.y1) - Math.max(Math.min(p.y, q.y), o.y0) > 1e-3),
    );
  const openEnd = (b: StairBlock, p: Vec2, q: Vec2) =>
    b.bridge !== undefined &&
    (b.bridge === 'x0' || b.bridge === 'x1'
      ? near(p.x, b[b.bridge]) && near(q.x, b[b.bridge])
      : near(p.y, b[b.bridge]) && near(q.y, b[b.bridge]));
  for (const b of landings) {
    const edges: [Vec2, Vec2][] = [
      [
        { x: b.x0, y: b.y0 },
        { x: b.x1, y: b.y0 },
      ],
      [
        { x: b.x1, y: b.y0 },
        { x: b.x1, y: b.y1 },
      ],
      [
        { x: b.x0, y: b.y1 },
        { x: b.x1, y: b.y1 },
      ],
      [
        { x: b.x0, y: b.y0 },
        { x: b.x0, y: b.y1 },
      ],
    ];
    for (const [p, q] of edges) {
      if (
        sharesEdge(Math.min(p.x, q.x), Math.min(p.y, q.y), Math.max(p.x, q.x), Math.max(p.y, q.y))
      )
        continue;
      if (againstWall(p, q) || sharesLanding(b, p, q) || openEnd(b, p, q)) continue;
      if (goesOn && b.exit && near(p.y, farEnd) && near(q.y, farEnd)) continue;
      // The exit landing opens onto a floor at its height (mezzanine, office storey).
      if (b.exit) {
        const mid = { x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 };
        const centre = { x: (b.x0 + b.x1) / 2, y: (b.y0 + b.y1) / 2 };
        const len = Math.hypot(mid.x - centre.x, mid.y - centre.y) || 1;
        const beyond = {
          x: mid.x + ((mid.x - centre.x) / len) * 0.3,
          y: mid.y + ((mid.y - centre.y) / len) * 0.3,
        };
        if (walkableAt(doc, world(beyond), base + b.top)) continue;
      }
      out.push(rail(p, q, b.top + STAIR_GUARD_HEIGHT, b.top + STAIR_GUARD_HEIGHT, 'guard'));
    }
  }
  return out;
}

/**
 * Door from the exit landing into the building: in the office wall next to the landing, at
 * the upper floor level. Null without an exit landing or when no wall is next to it.
 */
export function landingDoor(
  doc: Pick<ProjectDocument, 'buildings' | 'walls'>,
  s: Stair,
): { wallId: string; offset: number; width: number; sillHeight: number; height: number } | null {
  const b = doc.buildings[s.buildingId];
  const l = stairGeometry(doc, s);
  const landing = l.blocks.find((x) => x.exit);
  if (!b?.office || !landing) return null;
  const center = stairToWorld(s, {
    x: (landing.x0 + landing.x1) / 2,
    y: (landing.y0 + landing.y1) / 2,
  });
  const reach = Math.max(landing.x1 - landing.x0, landing.y1 - landing.y0) / 2 + 0.4;
  let best: { wall: Wall; d: number } | null = null;
  // The exterior wall of the storey the stair arrives on.
  for (const w of Object.values(doc.walls)) {
    if (w.ownerId !== b.id || w.level !== s.fromLevel + 1) continue;
    const d = distancePointToSegment(center, { a: w.start, b: w.end }) - w.thickness / 2;
    if (d <= reach && (!best || d < best.d)) best = { wall: w, d };
  }
  if (!best) return null;
  const w = best.wall;
  const f = wallFrame(w);
  const width = Math.min(1, s.width);
  const along = (center.x - w.start.x) * f.dir.x + (center.y - w.start.y) * f.dir.y;
  const sillHeight = 0;
  return {
    wallId: w.id,
    offset: Math.min(Math.max(along - width / 2, 0), Math.max(f.length - width, 0)),
    width,
    sillHeight,
    height: Math.max(0.5, Math.min(2.1, w.height - sillHeight)),
  };
}

export const STAIRWELL_WALL = 0.2;
/** Landing in front of the first step inside an enclosed stairwell. */
export const STAIRWELL_ENTRY = 1.2;
export const STAIRWELL_DOOR = 1;

export type StairwellSides = NonNullable<Stair['wallSides']>;
export const ALL_STAIRWELL_SIDES: StairwellSides = {
  front: true,
  back: true,
  left: true,
  right: true,
};

export type StairwellSide = keyof StairwellSides;

/**
 * Centrelines of the stairwell walls in the stair's local frame (front = at the first step,
 * beyond the entry landing): back and front run across, the sides along the whole stairwell.
 */
export function stairwellWallLines(
  l: StairLayout,
  sides: StairwellSides = ALL_STAIRWELL_SIDES,
  entry = STAIRWELL_ENTRY,
): { side: StairwellSide; a: Vec2; b: Vec2 }[] {
  const t = STAIRWELL_WALL;
  const x0 = -l.width / 2 - t / 2;
  const x1 = l.width / 2 + t / 2;
  const y0 = -l.depth / 2 - entry - t / 2;
  const y1 = l.depth / 2 + t / 2;
  const lines: { side: StairwellSide; a: Vec2; b: Vec2 }[] = [
    { side: 'back', a: { x: x1 + t / 2, y: y1 }, b: { x: x0 - t / 2, y: y1 } },
    { side: 'left', a: { x: x0, y: y1 - t / 2 }, b: { x: x0, y: y0 - t / 2 } },
    { side: 'right', a: { x: x1, y: y0 - t / 2 }, b: { x: x1, y: y1 - t / 2 } },
    { side: 'front', a: { x: x0 + t / 2, y: y0 }, b: { x: x1 - t / 2, y: y0 } },
  ];
  return lines.filter((w) => sides[w.side]);
}

/** Id of a stairwell wall piece: the stair's own storey, or (`top`) the storey it arrives on. */
export const stairwellWallId = (stairId: string, side: StairwellSide, top: boolean): string =>
  `${stairId}-sw-${side}${top ? '-top' : ''}`;

/** Key of a stairwell wall piece in `removedWalls`. */
export const stairwellWallKey = (side: StairwellSide, top: boolean): string =>
  `${side}${top ? '-top' : ''}`;

/**
 * Walls of an enclosed stairwell, as real walls of the storeys it passes (doors and windows go
 * into them): each stair walls its own storey, the highest stair of a stairwell also the
 * storey it arrives on. A storey whose outline does not reach the stairwell gets none.
 */
export function stairwellWallSet(
  doc: LevelDoc & Pick<ProjectDocument, 'stairs'>,
  s: Stair,
  color = DEFAULT_STAIRWELL_COLOR,
): Wall[] {
  const b = doc.buildings[s.buildingId];
  if (!s.enclosed || !b?.office) return [];
  const o = b.office;
  const l = stairGeometry(doc, s);
  const e = levelElevations(o);
  const sides = s.wallSides ?? ALL_STAIRWELL_SIDES;
  // A flight going on from the landing below in line is entered from there: no front wall.
  const from = stairInLine(doc, s, -1) ? { ...sides, front: false } : sides;
  const lines = stairwellWallLines(l, from, stairEntry(s));
  const removed = s.removedWalls ?? [];
  const highest = !Object.values(doc.stairs).some(
    (x) => x.groupId !== undefined && x.groupId === s.groupId && x.fromLevel === s.fromLevel + 1,
  );
  const storeys = [{ level: s.fromLevel, top: false }];
  if (highest && s.fromLevel + 1 < o.floors.length)
    storeys.push({ level: s.fromLevel + 1, top: true });
  return storeys.flatMap(({ level, top }) => {
    if (!pointInPolygon(s.center, storeyCorners(b, level))) return [];
    const height = o.floors[level]?.clearHeight ?? (e[level + 1] ?? 0) - (e[level] ?? 0);
    return lines.flatMap((w): Wall[] =>
      removed.includes(stairwellWallKey(w.side, top))
        ? []
        : [
            {
              id: stairwellWallId(s.id, w.side, top),
              start: stairToWorld(s, w.a),
              end: stairToWorld(s, w.b),
              thickness: STAIRWELL_WALL,
              height,
              baseElevation: 0,
              level,
              kind: 'interior',
              cladding: 'plain',
              ownerId: s.id,
              color,
              locked: false,
            },
          ],
    );
  });
}

/** Stair and piece of a stairwell wall (by its id), or null. */
export function stairwellWallOf(
  doc: Pick<ProjectDocument, 'stairs'>,
  wall: Pick<Wall, 'id' | 'ownerId'>,
): { stair: Stair; side: StairwellSide; top: boolean } | null {
  const s = wall.ownerId ? doc.stairs[wall.ownerId] : undefined;
  if (!s) return null;
  const m = /-sw-(front|back|left|right)(-top)?$/.exec(wall.id);
  if (!m) return null;
  return { stair: s, side: m[1] as StairwellSide, top: m[2] !== undefined };
}

export const DEFAULT_STAIRWELL_COLOR = '#d9d6cf';

/** Readable planning hints for a stair (ASR A1.8 guide values). */
export function stairHints(l: StairLayout, width: number): string[] {
  const out: string[] = [];
  const t = modelTexts().stairHints;
  if (l.riserHeight > STAIR_RULES.riserMax + 1e-9) out.push(t.steep);
  const step = 2 * l.riserHeight + l.going;
  if (step < STAIR_RULES.stepMin - 1e-9 || step > STAIR_RULES.stepMax + 1e-9)
    out.push(fillText(t.stepRule, { value: formatNumber(step * 100, 1, 1) }));
  if (width < STAIR_RULES.widthMin - 1e-9) out.push(t.narrow);
  // Risers of the longest flight: its treads plus the step onto the landing or floor.
  const risers = Math.round(l.runLength / l.going) + 1;
  if (risers > MAX_RISERS_PER_FLIGHT) out.push(fillText(t.landingNeeded, { n: risers }));
  return out;
}

/** More risers in one flight need an intermediate landing (ASR A1.8: 18 at most). */
export const MAX_RISERS_PER_FLIGHT = 18;
